/**
 * useTelephonyHotkeys Hook
 *
 * @module shared/hooks/useTelephonyHotkeys
 *
 * Global keyboard accelerators for call controls.
 *
 * - Alt combos (matched on `event.code`, so they work on layouts where the
 *   Alt/Option key rewrites the produced character):
 *   Alt+A answer, Alt+H hang up, Alt+M mute, Alt+P hold, Alt+N next call, Alt+S submit
 * - Plain keys: Enter answers the call, Escape hangs it up.
 *
 * A handler that is `undefined` disables the corresponding shortcut: the key is
 * neither consumed (`preventDefault`) nor triggered.
 */

import { useEffect, useRef } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface TelephonyHotkeyHandlers {
  onAnswer?: () => void;
  onHangUp?: () => void;
  onToggleMute?: () => void;
  onToggleHold?: () => void;
  onNextCall?: () => void;
  onSubmit?: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

/** Elements where typing takes precedence over the global shortcuts. */
const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable]';

/** Elements that handle Enter themselves (e.g. buttons, links). */
const INTERACTIVE_SELECTOR = 'button, a, [role="button"]';

function matchesSelector(target: EventTarget | null, selector: string): boolean {
  if (!target || !(target instanceof Element)) return false;
  return target.closest(selector) !== null;
}

function isEditableTarget(target: EventTarget | null): boolean {
  return matchesSelector(target, EDITABLE_SELECTOR);
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return matchesSelector(target, INTERACTIVE_SELECTOR);
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Registers global keyboard accelerators for telephony actions.
 *
 * @param handlers - Action callbacks. Omit (or pass `undefined`) to disable a shortcut.
 */
export function useTelephonyHotkeys(handlers: TelephonyHotkeyHandlers): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.defaultPrevented) return;

      const current = handlersRef.current;

      // Alt combos — matched on event.code so Option (macOS) still resolves to the same key.
      if (event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        let altAction: (() => void) | undefined;
        switch (event.code) {
          case 'KeyA':
            altAction = current.onAnswer;
            break;
          case 'KeyH':
            altAction = current.onHangUp;
            break;
          case 'KeyM':
            altAction = current.onToggleMute;
            break;
          case 'KeyP':
            altAction = current.onToggleHold;
            break;
          case 'KeyN':
            altAction = current.onNextCall;
            break;
          case 'KeyS':
            altAction = current.onSubmit;
            break;
          default:
            return;
        }

        if (!altAction) return;
        event.preventDefault();
        altAction();
        return;
      }

      // Plain keys — Enter answers, Escape hangs up. Only unmodified presses count.
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

      if (isEditableTarget(event.target)) return;

      if (event.key === 'Enter') {
        if (isInteractiveTarget(event.target)) return;
        if (!current.onAnswer) return;
        event.preventDefault();
        current.onAnswer();
        return;
      }

      if (event.key === 'Escape') {
        if (!current.onHangUp) return;
        event.preventDefault();
        current.onHangUp();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);
}
